import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import ShopNav from "@/components/ShopNav";
import Footer from "@/components/sections/Footer";
import BundleSelector from "@/components/BundleSelector";
import { useLang } from "@/i18n/LanguageContext";
import { useCart } from "@/context/CartContext";
import { Bell, ShieldCheck, Package, Truck, MessageCircle, Minus, Plus, Check, Leaf } from "lucide-react";
import Seo, { productLd, breadcrumbLd, getSiteUrl } from "@/components/Seo";
import { PRE_HARVEST_MODE } from "@/lib/siteMode";
import WaitlistForm from "@/components/WaitlistForm";

const SITE_URL = getSiteUrl();
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TRUST = [Package, ShieldCheck, Truck, MessageCircle];

const BOX_SLUG = "estuche-madera-2x500ml";
const BUNDLE_SLUGS = ["estuche-mas-1-botella", "estuche-mas-2-botellas"];

export default function ProductPage({ forceLang }) {
  const { slug } = useParams();
  const { t, lang, setLang } = useLang();
  useEffect(() => {
    if (forceLang && forceLang !== lang) setLang(forceLang);
  }, [forceLang, lang, setLang]);
  const effectiveLang = forceLang || lang;
  const { addItem } = useCart();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [error, setError] = useState(null);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [bundleVariants, setBundleVariants] = useState([]);
  const [selectedBundleId, setSelectedBundleId] = useState(null);
  const [showWaitlist, setShowWaitlist] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/products/${slug}`)
      .then((res) => {
        setProduct(res.data);
        setError(null);
      })
      .catch(() => setError(t("common.error_generic")));
  }, [slug, t]);

  // When viewing the box page, fetch the two hidden bundle variants in parallel.
  useEffect(() => {
    if (slug !== BOX_SLUG) return undefined;
    let cancelled = false;
    Promise.all(BUNDLE_SLUGS.map((s) => axios.get(`${API}/products/${s}`).then((r) => r.data)))
      .then((arr) => {
        if (!cancelled) setBundleVariants(arr);
      })
      .catch(() => {
        // Bundles missing → graceful fallback (selector hidden, page works as before).
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Bundle selector only applies on the gift-box product page.
  const showBundleSelector =
    slug === BOX_SLUG && product && bundleVariants.length === BUNDLE_SLUGS.length;

  // Default to the base product when no explicit selection has been made.
  const effectiveSelectedId = selectedBundleId ?? product?.id ?? null;

  const bundleOptions = useMemo(() => {
    if (!showBundleSelector || !product) return [];
    return [
      {
        id: product.id,
        slug: product.slug,
        label: t("product_page.bundle_option_box"),
        price: product.price,
        bottle_count: product.bottle_count || 2,
      },
      ...bundleVariants.map((v, idx) => ({
        id: v.id,
        slug: v.slug,
        label: idx === 0
          ? t("product_page.bundle_option_box_1")
          : t("product_page.bundle_option_box_2"),
        price: v.price,
        bottle_count: v.bottle_count,
      })),
    ];
  }, [showBundleSelector, product, bundleVariants, t]);

  if (error)
    return (
      <main className="min-h-screen bg-cream-100">
        <ShopNav />
        <div className="max-w-3xl mx-auto px-6 py-32 text-center">
          <p className="text-olive-800">{error}</p>
          <Link to="/tienda" className="mt-6 inline-block underline text-olive-900">
            {t("shop_page.overline")}
          </Link>
        </div>
      </main>
    );
  if (!product)
    return (
      <main className="min-h-screen bg-cream-100">
        <ShopNav />
        <div className="max-w-3xl mx-auto px-6 py-32 text-olive-800/60">{t("common.loading")}</div>
      </main>
    );

  const isEs = effectiveLang === "es";
  const name = isEs ? product.name_es : product.name_en;
  const longDesc = isEs ? product.long_desc_es : product.long_desc_en;
  const highlights = isEs ? product.highlights_es : product.highlights_en;

  // The "active" SKU drives price display and add-to-cart action.
  const activeSku = (() => {
    if (!showBundleSelector || effectiveSelectedId === product.id) return product;
    return bundleVariants.find((v) => v.id === effectiveSelectedId) || product;
  })();

  const soldOut = activeSku.stock <= 0;
  const lowStock =
    activeSku.stock > 0 && activeSku.stock <= (activeSku.low_stock_threshold || 6);

  const handleAdd = () => {
    if (soldOut) return;
    addItem(activeSku, qty);
    setAdded(true);
    setTimeout(() => setAdded(false), 1600);
  };

  const buyNow = () => {
    if (soldOut) return;
    addItem(activeSku, qty);
    navigate("/carrito");
  };

  const productBase = isEs ? "/producto/" : "/product/";
  const canonical = `${SITE_URL}${productBase}${product.slug}`;
  const alternates = {
    es: `${SITE_URL}/producto/${product.slug}`,
    en: `${SITE_URL}/product/${product.slug}`,
    "x-default": `${SITE_URL}/producto/${product.slug}`,
  };

  return (
    <main data-testid="product-page" className="min-h-screen bg-cream-100 text-charcoal">
      <Seo
        title={
          // Avoid stuttering the brand: if the product name already starts
          // with "Cortijo Santa María", don't append the suffix. Otherwise
          // append the brand for context (e.g. "Botella 500 ml | Cortijo…").
          /^cortijo santa mar[íi]a/i.test(name)
            ? name
            : `${name} | Cortijo Santa María`
        }
        description={isEs ? product.short_desc_es : product.short_desc_en}
        canonical={canonical}
        alternates={alternates}
        ogImage={product.image_url}
        lang={effectiveLang}
        ogType="product"
        jsonLd={[
          productLd(product, canonical, effectiveLang, false),
          breadcrumbLd([
            { name: isEs ? "Inicio" : "Home", url: `${SITE_URL}${isEs ? "/" : "/en"}` },
            { name: isEs ? "Tienda" : "Shop", url: `${SITE_URL}${isEs ? "/tienda" : "/shop"}` },
            { name, url: canonical },
          ]),
        ]}
      />
      <ShopNav />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-10 sm:pt-16 pb-24 grid lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-6">
          <div className="aspect-[4/5] bg-cream-200 overflow-hidden border border-cream-400">
            <img
              src={product.image_url}
              alt={name}
              className="h-full w-full object-contain p-10 sm:p-16"
            />
          </div>
        </div>

        <div className="lg:col-span-6">
          <nav className="text-[11px] uppercase tracking-[0.24em] text-olive-800/50 mb-4">
            <Link to="/" className="hover:text-olive-900">
              {t("nav_ecom.home")}
            </Link>{" "}
            ·{" "}
            <Link to="/tienda" className="hover:text-olive-900">
              {t("nav_ecom.shop")}
            </Link>
          </nav>
          <h1 data-testid="product-name" className="font-serif text-4xl sm:text-5xl leading-[1.08] text-olive-900">
            {name}
          </h1>

          <div className="mt-6 flex items-baseline flex-wrap gap-x-4 gap-y-2">
            {PRE_HARVEST_MODE ? (
              <span
                data-testid="product-preharvest-badge"
                className="text-[11px] uppercase tracking-[0.24em] bg-stone-700 text-cream-100 px-3 py-1"
              >
                {effectiveLang === "es"
                  ? "Próxima cosecha · aún no disponible"
                  : "Next harvest · not yet available"}
              </span>
            ) : (
              <>
                <div
                  data-testid="product-price"
                  className="font-serif text-4xl text-olive-900 tabular-nums"
                >
                  {activeSku.price.toFixed(2).replace(".", ",")}{" "}
                  <span className="text-sm text-olive-800/60 font-sans font-light">€</span>
                </div>
                <span
                  data-testid="product-price-per-litre"
                  className="text-sm text-olive-800/60 font-sans font-light tabular-nums"
                >
                  ({(activeSku.price / (0.5 * (activeSku.bottle_count || 1))).toFixed(0)} €/l)
                </span>
                {soldOut && (
                  <span className="text-[11px] uppercase tracking-[0.24em] bg-charcoal text-cream-100 px-3 py-1">
                    {t("product_page.out_of_stock")}
                  </span>
                )}
                {lowStock && (
                  <span className="text-[11px] uppercase tracking-[0.24em] bg-gold text-olive-900 px-3 py-1">
                    {t("product_page.low_stock")}
                  </span>
                )}
              </>
            )}
          </div>
          {!PRE_HARVEST_MODE && (
            <div
              data-testid="product-vat-included"
              className="mt-2 text-[10px] uppercase tracking-[0.22em] text-olive-800/55 font-light"
            >
              {t("product_page.vat_included")}
            </div>
          )}

          <div
            data-testid="product-availability-note"
            role="note"
            className="mt-6 flex items-start gap-3 border-l-2 border-gold-deep bg-cream-200/70 px-4 py-3"
          >
            <Leaf size={15} strokeWidth={1.3} className="text-gold-deep mt-0.5 shrink-0" />
            <p className="text-sm text-olive-900/85 font-light leading-relaxed">
              {t("product_page.availability_note")}
            </p>
          </div>

          <p className="mt-6 text-olive-800/80 font-light leading-relaxed">{longDesc}</p>

          {PRE_HARVEST_MODE && (
            <p
              data-testid="pdp-reference-note"
              className="mt-5 text-xs text-olive-800/60 border-l-2 border-gold-deep pl-3 italic"
            >
              {effectiveLang === "es"
                ? "Datos de referencia de la campaña anterior. Los datos definitivos de la próxima edición se confirmarán tras la cosecha, elaboración, analítica y embotellado."
                : "Reference data from the previous harvest. Final details for the next edition will be confirmed after harvest, production, analysis and bottling."}
            </p>
          )}

          {highlights?.length > 0 && (
            <ul className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-3">
              {highlights.map((h) => (
                <li key={h} className="flex items-start gap-3 text-sm text-olive-800/90 font-light">
                  <span className="mt-0.5 w-5 h-5 inline-flex items-center justify-center border border-gold-deep/50 rounded-full shrink-0">
                    <Check size={11} strokeWidth={2} className="text-gold-deep" />
                  </span>
                  <span>{h}</span>
                </li>
              ))}
            </ul>
          )}

          {!PRE_HARVEST_MODE && showBundleSelector && (
            <BundleSelector
              options={bundleOptions}
              selectedId={effectiveSelectedId}
              onSelect={setSelectedBundleId}
            />
          )}

          {/* Qty + CTA */}
          {PRE_HARVEST_MODE ? (
            <div className="mt-10">
              <button
                type="button"
                data-testid="product-waitlist-btn"
                onClick={() => setShowWaitlist(true)}
                className="inline-flex items-center gap-3 px-8 py-4 bg-olive-700 text-cream-100 text-[12px] uppercase tracking-[0.26em] hover:bg-olive-800 transition-colors"
              >
                <Bell size={14} strokeWidth={1.4} />
                {effectiveLang === "es"
                  ? "Avísame cuando esté disponible"
                  : "Notify me when it is available"}
              </button>
              <p className="mt-3 text-xs text-olive-800/60 font-light">
                {effectiveLang === "es"
                  ? "Aviso de disponibilidad · Te informaremos cuando abramos pedidos."
                  : "Availability notice · We will let you know when orders open."}
              </p>
            </div>
          ) : (
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <div className="inline-flex items-center border border-olive-700/40">
                <button
                  type="button"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="px-3 py-3 text-olive-800 hover:bg-cream-200"
                  aria-label="minus"
                  data-testid="product-qty-minus"
                >
                  <Minus size={14} strokeWidth={1.5} />
                </button>
                <span
                  data-testid="product-qty"
                  className="px-5 font-serif text-olive-900 text-lg min-w-[40px] text-center"
                >
                  {qty}
                </span>
                <button
                  type="button"
                  onClick={() => setQty((q) => Math.min(product.stock || 99, q + 1))}
                  className="px-3 py-3 text-olive-800 hover:bg-cream-200"
                  aria-label="plus"
                  data-testid="product-qty-plus"
                >
                  <Plus size={14} strokeWidth={1.5} />
                </button>
              </div>
              <button
                onClick={handleAdd}
                disabled={soldOut}
                data-testid="product-add-to-cart"
                className="inline-flex items-center gap-2 px-8 py-4 bg-olive-700 text-cream-100 text-[12px] uppercase tracking-[0.26em] hover:bg-olive-800 disabled:bg-cream-400 disabled:text-olive-800/50 disabled:cursor-not-allowed transition-colors"
              >
                {added ? (
                  <>
                    <Check size={15} strokeWidth={1.5} />
                    {t("product_page.added")}
                  </>
                ) : (
                  t("product_page.add_to_cart")
                )}
              </button>
              <button
                onClick={buyNow}
                disabled={soldOut}
                data-testid="product-buy-now"
                className="inline-flex items-center gap-2 px-8 py-4 border border-olive-700 text-olive-800 text-[12px] uppercase tracking-[0.26em] hover:bg-cream-200 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {t("cart_page.checkout")}
              </button>
            </div>
          )}

          <p className="mt-4 text-xs text-olive-800/60">
            {PRE_HARVEST_MODE
              ? (effectiveLang === "es"
                  ? "Información de la próxima campaña pendiente de confirmación tras cosecha, elaboración, analítica y embotellado."
                  : "Next edition details pending confirmation after harvest, milling, laboratory analysis and bottling.")
              : t("product_page.shipping_info")}
          </p>

          {showBundleSelector && (
            <p
              data-testid="bundle-microcopy"
              className="mt-3 text-xs text-olive-800/55 font-light italic"
            >
              {t("product_page.bundle_micro")}
            </p>
          )}

          {/* Trust row */}
          <div className="mt-10 grid grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => {
              if (PRE_HARVEST_MODE && i === 3) return null; // hide payment trust in pre-harvest
              const Icon = TRUST[i - 1];
              return (
                <div
                  key={i}
                  className="flex items-start gap-3 p-4 border border-cream-400"
                >
                  <Icon size={18} strokeWidth={1.2} className="text-gold-deep mt-0.5" />
                  <span className="text-sm text-olive-800/85 font-light">
                    {t(`product_page.trust_${i}`)}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Attributes */}
          {product.attributes?.length > 0 && (
            <div className="mt-12 border-t border-cream-400 pt-8">
              <h3 className="font-serif text-xl text-olive-900 mb-4">
                {t("product_page.attributes_title")}
              </h3>
              <dl className="grid sm:grid-cols-2 gap-y-3 gap-x-8">
                {product.attributes.map((a) => (
                  <div key={a.k_es || a.k_en} className="flex flex-col">
                    <dt className="text-[10px] uppercase tracking-[0.28em] text-olive-800/60">
                      {isEs ? a.k_es : a.k_en}
                    </dt>
                    <dd className="mt-1 font-serif text-olive-900 text-base">
                      {isEs ? a.v_es : a.v_en}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </div>
      </div>
      <Footer />
      {showWaitlist && (
        <WaitlistForm
          onClose={() => setShowWaitlist(false)}
          preselectedProduct={product?.slug || ""}
        />
      )}
    </main>
  );
}
